import type { UserWebTracker as UserWebTrackerType, ITrackingParam } from '@socialtechnologies/tracking-lib'

import Http from '@/api/Http'
import {
  ActionType,
  EventSubtypes,
  EventType,
  ITrackingEvent,
  ITrackingSource,
  PageType,
  TrackingObjectName,
  TrackingOriginName,
  SourceObjectContent,
} from '@/types/events'

import ErrorTrackingService from './ErrorTrackingService'

class TrackingService {
  private static readonly TRACK_SOURCE_DEBOUNCE_TIME = 200
  private static readonly MAX_RETRY_ATTEMPTS = 10
  private static readonly RETRY_DELAY = 100

  private isInited = false
  private addUserIdentities: (
    (content: SourceObjectContent) => SourceObjectContent
  ) | null = null

  private tracker: UserWebTrackerType<
    EventType,
    ActionType,
    PageType,
    TrackingObjectName,
    TrackingOriginName
  > | null = null

  async init(): Promise<void> {
    try {
      const { UserWebTracker } = await import('@socialtechnologies/tracking-lib')

      this.addUserIdentities = (await import('@/utils/userIdentities')).addUserIdentities

      this.tracker = new UserWebTracker()
      await this.tracker.init(
        this.getTrackingEndpoint(),
        TrackingService.TRACK_SOURCE_DEBOUNCE_TIME,
      )

      this.isInited = true
    } catch (error) {
      ErrorTrackingService.captureMessage('TrackingService: tracker.init() failed in init()', { extra: { error } })
    }
  }

  private getTrackingEndpoint(): string {
    return `${Http.domain}`
  }

  private getEventData(
    action: ActionType,
    subtype: string,
    params: ITrackingParam[] = [],
  ): ITrackingEvent {
    return {
      type: EventType.Payments,
      params,
      action,
      subtype,
    }
  }

  private getSourceData(object: SourceObjectContent = []): ITrackingSource {
    const objectWithUserAndSessionInfo = this.addUserIdentities!(object)

    return {
      pageType: PageType.DescriptorPage,
      pageUrl: window.location.href,
      targetUserId: 0,
      object: {
        name: TrackingObjectName.DescriptorPage,
        content: objectWithUserAndSessionInfo,
      },
    }
  }

  // eslint-disable-next-line max-params
  async trackSource(
    eventType: EventType,
    eventData: ITrackingEvent,
    sourceObjectContent: SourceObjectContent,
    noResultTracking?: boolean,
    retryAttempt = 0,
  ): Promise<void> {
    if (!this.isInited || !this.tracker || !this.addUserIdentities) {
      if (retryAttempt >= TrackingService.MAX_RETRY_ATTEMPTS) {
        return
      }

      setTimeout(() => {
        this.trackSource(
          eventType,
          eventData,
          sourceObjectContent,
          noResultTracking,
          retryAttempt + 1,
        )
      }, TrackingService.RETRY_DELAY)

      return
    }

    try {
      const sourceData = this.getSourceData(sourceObjectContent)

      await this.tracker.publicTrackEventSource(eventType, eventData, sourceData)
    } catch (error) {
      ErrorTrackingService.captureMessage('TrackingService: tracker.publicTrackEventSource() failed in trackSource()', { extra: { error } })
    }
  }

  trackComponentInteraction(
    action: ActionType,
    sourceObjectContent: SourceObjectContent,
    eventSubtype: EventSubtypes,
  ): void {
    this.trackSource(
      EventType.Payments,
      this.getEventData(action, eventSubtype),
      sourceObjectContent,
    )
  }
}

export default new TrackingService()
